import { PerformanceDiagnostics } from './PerformanceDiagnostics';
import { metricDefinitions, type PerformanceMetric } from './performanceSchema';
import { buildInfo } from '@/buildInfo';
import { IndexedDbDiagnosticStore } from './IndexedDbDiagnosticStore';
import { LocalDiagnostics } from './LocalDiagnostics';

export const diagnostics = new LocalDiagnostics(
  new IndexedDbDiagnosticStore(),
  buildInfo.commit,
);
export const performanceDiagnostics = new PerformanceDiagnostics((summary) =>
  diagnostics.recordTiming(summary),
);
export const performanceSupport = {
  longTask: 'not-installed',
  interaction: 'not-installed',
};
const observerReaders = new Map<
  PerformanceObserver,
  (entries: PerformanceEntry[]) => void
>();
export function flushPerformanceDiagnostics() {
  for (const [observer, read] of observerReaders) read(observer.takeRecords());
  performanceDiagnostics.flush();
}
export function clearPerformanceDiagnostics() {
  for (const observer of observerReaders.keys()) observer.takeRecords();
  performanceDiagnostics.clear();
}
export function performanceMetadata() {
  return {
    support: { ...performanceSupport },
    definitions: metricDefinitions,
    sampling:
      'At most one timed operation per metric per second; nested same-metric calls excluded. Observer samples limited to 32 per callback. Aggregates flush every minute or on download. Individual durations clip at 300000 ms; each aggregate caps at 100000 samples.',
    interpretation:
      'Durations are elapsed wall time, not CPU time. Synchronous samples include GC and nested work. Interaction counts are EventTiming entry samples (minimum 104 ms, browser-rounded), not unique interactions or INP; long tasks are entries of at least 50 ms. These are browser estimates, not exact rendering time. Missing metric records mean no retained samples, not zero cost. Simulation-input measures synchronous external-input dispatch/propagation (not deferred ticks); simulation-drain measures explicit native convergence only; continuous worker compute and exact GPU/frame timing are not measured. Editor-sync measures action/undo/redo bodies; deferred reactive work is reported separately as graph-rebuild. Workload is the largest coarse node-count bucket observed in a summary; no circuit data is included.',
  };
}
export function installDiagnosticCapture(target: Window = window) {
  // Deliberately never inspect arbitrary Error/rejection objects, even their getters.
  const error = () => diagnostics.record('runtime', 'unhandled');
  target.addEventListener('error', error);
  target.addEventListener('unhandledrejection', error);
  // Prune expired disk history at startup, then hourly while the app is open.
  void diagnostics.flush();
  const observers: PerformanceObserver[] = [];
  for (const [entryType, metric, key] of [
    ['longtask', 'long-task', 'longTask'],
    ['event', 'interaction', 'interaction'],
  ] as const) {
    try {
      if (
        typeof PerformanceObserver === 'undefined' ||
        !PerformanceObserver.supportedEntryTypes.includes(entryType)
      ) {
        performanceSupport[key] = 'unsupported';
        continue;
      }
      const read = (entries: PerformanceEntry[]) => {
        if (target.document?.visibilityState === 'hidden') return;
        for (let i = 0; i < Math.min(entries.length, 32); i++)
          performanceDiagnostics.observe(
            metric as PerformanceMetric,
            entries[i].duration,
          );
      };
      const observer = new PerformanceObserver((list) => read(list.getEntries()));
      observer.observe({
        type: entryType,
        buffered: false,
        ...(entryType === 'event' ? { durationThreshold: 104 } : {}),
      });
      observers.push(observer);
      observerReaders.set(observer, read);
      performanceSupport[key] = 'supported';
    } catch {
      performanceSupport[key] = 'unavailable';
    }
  }
  const aggregateTimer = setInterval(() => performanceDiagnostics.flush(), 60000);
  const retention = setInterval(() => void diagnostics.flush(), 3600000);
  return () => {
    target.removeEventListener('error', error);
    target.removeEventListener('unhandledrejection', error);
    clearInterval(retention);
    clearInterval(aggregateTimer);
    observers.forEach((observer) => {
      observer.disconnect();
      observerReaders.delete(observer);
    });
  };
}
